import { useState } from "react";
import { clientApi, getApiErrorMessage } from "@/lib/api";
import { ClientLayout } from "@/components/layout/ClientLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/StatusBadge";
import { formatCurrency, formatDate } from "@/lib/format";
import { toast } from "sonner";
import { FileText, Download } from "lucide-react";

export default function Statements() {
  const now = new Date();
  const [from, setFrom] = useState(`01/01/${now.getFullYear()}`);
  const [to, setTo] = useState(`${String(now.getDate()).padStart(2, "0")}/${String(now.getMonth() + 1).padStart(2, "0")}/${now.getFullYear()}`);
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState("order-status");
  const [rows, setRows] = useState([]);
  const [source, setSource] = useState(null);

  const fetchReport = async () => {
    setLoading(true);
    try {
      if (reportType === "rta-transactions") {
        const { data } = await clientApi.get("/portfolio/transactions?limit=5000");
        const mapped = (data.transactions || []).map((row) => ({
          order_number: row.transaction_no || row.user_transaction_no || "—",
          scheme_name: row.scheme_name, scheme_code: row.amfi_scheme_code,
          order_type: row.canonical_type, amount: row.amount, qty: row.units,
          folio_no: row.folio_no, status: row.record_status || "POSTED",
          created_at: row.trade_date || row.post_date, holding_mode: row.holding_mode,
          rta_source: row.source,
        }));
        setRows(mapped);
        setSource({ source: data.source });
        toast.success(`CAMS/KFintech history loaded: ${mapped.length} record(s)`);
        return;
      }
      const { data } = await clientApi.get(`/reports/${reportType}`, { params: { from_date: from, to_date: to } });
      setRows(data.data || []);
      setSource({ source: data.source, sync: data.sync });
      toast.success(`BSE statement refreshed: ${(data.data || []).length} record(s)`);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = () => {
    if (rows.length === 0) return;
    const headers = Object.keys(rows[0]).filter((k) => typeof rows[0][k] !== "object");
    const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => JSON.stringify(r[h] ?? "")).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${reportType}_statement.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <ClientLayout>
      <div className="mb-6 flex items-center gap-3">
        <FileText className="h-6 w-6 text-[var(--cta)]" />
        <h1 className="font-heading text-3xl text-[var(--brand)]">Statements</h1>
      </div>

      <Tabs value={reportType} onValueChange={setReportType} className="mb-4">
        <TabsList>
          <TabsTrigger value="order-status" data-testid="statement-tab-order-status">Order Status</TabsTrigger>
          <TabsTrigger value="allotment" data-testid="statement-tab-allotment">Allotment</TabsTrigger>
          <TabsTrigger value="redemption" data-testid="statement-tab-redemption">Redemption</TabsTrigger>
          <TabsTrigger value="rta-transactions">CAMS / KFintech history</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="mb-6 border-[var(--glass-border)]">
        <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-end">
          <div className="space-y-1.5"><Label>From (DD/MM/YYYY)</Label><Input value={from} onChange={(e) => setFrom(e.target.value)} data-testid="statement-from-date-input" /></div>
          <div className="space-y-1.5"><Label>To (DD/MM/YYYY)</Label><Input value={to} onChange={(e) => setTo(e.target.value)} data-testid="statement-to-date-input" /></div>
          <Button onClick={fetchReport} disabled={loading} className="rounded-full bg-[var(--cta)] hover:bg-[#087a56]" data-testid="statement-fetch-button">{loading ? "Fetching..." : "Fetch statement"}</Button>
          {rows.length > 0 && <Button variant="outline" className="rounded-full" onClick={downloadCsv} data-testid="statement-download-button"><Download className="mr-2 h-4 w-4" />Download CSV</Button>}
        </CardContent>
      </Card>

      {source && <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
        {source.source === "CAMS_KFINTECH_RTA" ? "Source: authoritative CAMS / KFintech transaction feeds" : <>Source: BSE StAR MF 2.0 sync · {source.sync?.matched_ucc || 0} exchange order(s) matched across your UCCs · refreshed {source.sync?.at?.replace("T", " ").slice(0, 19) || "now"}</>}
      </div>}

      <Card className="border-[var(--glass-border)]">
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <div className="p-6 text-sm text-[var(--ink-muted)]" data-testid="statement-empty-message">No records. Try fetching a statement above.</div>
          ) : (
            <div className="divide-y divide-[var(--glass-border)]">
              {rows.map((r, i) => (
                <div key={i} className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`statement-row-${i}`}>
                  <div className="min-w-0">
                    <div className="font-medium">{r.scheme_name}</div>
                    <div className="text-xs text-[var(--ink-muted)]">{r.order_type} · folio {r.folio_no || "—"} · {r.holding_mode || "PHYSICAL"} · {r.rta_source || "BSE"} · {formatDate(r.created_at)}</div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="font-medium">{r.amount ? formatCurrency(r.amount) : `${r.qty} units`}</div>
                    <StatusBadge status={r.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </ClientLayout>
  );
}
